import React, { createContext, useContext, useState, useCallback } from 'react';
import './useSuccessModal.css';

interface SuccessModalOptions {
  title: string;
  message: string;
  buttonText?: string;
}

interface SuccessModalContextValue {
  showSuccess: (options: SuccessModalOptions) => void;
  // internal — used by GlobalSuccessModal only
  _state: { show: boolean; title: string; message: string; buttonText: string };
  _hide: () => void;
}

const SuccessModalContext = createContext<SuccessModalContextValue | null>(null);

export const SuccessModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState({ show: false, title: '', message: '', buttonText: 'Done' });

  const showSuccess = useCallback(({ title, message, buttonText = 'Done' }: SuccessModalOptions) => {
    setState({ show: true, title, message, buttonText });
  }, []);

  const hide = useCallback(() => {
    setState(prev => ({ ...prev, show: false }));
  }, []);

  return (
    <SuccessModalContext.Provider value={{ showSuccess, _state: state, _hide: hide }}>
      {children}
    </SuccessModalContext.Provider>
  );
};

export const useSuccessModal = () => {
  const ctx = useContext(SuccessModalContext);
  if (!ctx) throw new Error('useSuccessModal must be used inside SuccessModalProvider');
  return ctx.showSuccess;
};

export const useSuccessModalState = () => {
  const ctx = useContext(SuccessModalContext);
  if (!ctx) throw new Error('useSuccessModal must be used inside SuccessModalProvider');
  return { state: ctx._state, hide: ctx._hide };
};
